
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UserRound, Loader2 } from 'lucide-react';
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from '@/contexts/AuthContext';
import { toast } from "@/components/ui/use-toast";
import { useAppState } from '@/hooks/use-app-state';

interface UserSelectorProps {
  currentUserId: string | null;
  onSelect: (userId: string | null) => void;
  contextLabel?: string;
}

const UserSelector = ({ 
  currentUserId, 
  onSelect, 
  contextLabel = "Viendo:"
}: UserSelectorProps) => {
  const [users, setUsers] = useState<Array<{id: string, name: string, organization: string}>>([]);
  const [loading, setLoading] = useState(true);
  const { isAdmin, user } = useAuth();
  const { selectedViewUser, setSelectedViewUser } = useAppState();
  const [hasInitialized, setHasInitialized] = useState(false);
  const [validUserIds, setValidUserIds] = useState<Set<string>>(new Set());

  // If not admin, this component should not be visible
  if (!isAdmin) {
    return null;
  }
  
  // Fetch users with validation to ensure they exist in profiles table
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      console.log("Fetching primary users and admins...");
      
      // Get valid users who have records in the profiles table and usuario_focusflow = 'Si'
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, role, organization_name')
        .eq('usuario_focusflow', 'Si')
        .in('role', ['Usuario Principal', 'Administrador'])
        .order('first_name');
      
      if (error) throw error;
      
      // Create a Set of valid user IDs for quick lookups
      const validIds = new Set(data.map(profile => profile.id));
      setValidUserIds(validIds);
      
      const formattedUsers = data.map(user => ({
        id: user.id,
        name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Usuario sin nombre',
        organization: user.organization_name || 'Sin organización'
      }));
      
      console.log(`Found ${formattedUsers.length} valid users with appropriate roles in the profiles table`);
      setUsers(formattedUsers);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudieron cargar los usuarios."
      });
      setLoading(false);
    }
  }, []);
  
  // Initial fetch and setup
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);
  
  // Add a separate effect for initialization to prevent infinite loops
  useEffect(() => {
    // Only run once and only if not already initialized
    if (!hasInitialized && !loading && users.length > 0 && !currentUserId && selectedViewUser) {
      // Check if the previously selected user is still valid
      if (validUserIds.has(selectedViewUser)) {
        console.log(`Restoring user selection to: ${selectedViewUser}`);
        setHasInitialized(true);
        onSelect(selectedViewUser);
      } else {
        console.log(`Previously selected user ${selectedViewUser} is no longer valid, resetting selection`);
        setHasInitialized(true);
        onSelect(null);
      }
    }
  }, [currentUserId, selectedViewUser, onSelect, hasInitialized, loading, users, validUserIds]);

  // Ensure currentUserId has a valid value for the Select component
  const safeCurrentUserId = currentUserId || (user?.id || 'all');

  const handleUserChange = (value: string) => {
    console.log("User selected:", value);
    // Use 'all' to represent viewing all organizations
    const userId = value === 'all' ? null : value;
    
    // Validate that the user exists in profiles before selecting
    if (userId === null || validUserIds.has(userId)) {
      // Only update if the value has changed
      if (userId !== currentUserId) {
        // Update the global app state
        setSelectedViewUser(userId);
        // Call the component's onSelect callback
        onSelect(userId);
      }
    } else {
      toast({
        variant: "destructive",
        title: "Error",
        description: "El usuario seleccionado no es válido o no existe en el sistema."
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-2 text-sm text-foreground/60">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Cargando usuarios...</span>
      </div>
    );
  }

  return (
    <div className="flex items-center">
      <div className="text-sm mr-2 text-foreground/80">{contextLabel}</div>
      <Select 
        value={safeCurrentUserId} 
        onValueChange={handleUserChange}
      >
        <SelectTrigger className="w-[220px] h-8 text-sm bg-background border border-border">
          <UserRound className="mr-2 h-4 w-4 text-primary" />
          <SelectValue placeholder="Todas las organizaciones" />
        </SelectTrigger>
        <SelectContent className="bg-background border border-border shadow-md max-h-60 overflow-y-auto">
          <SelectItem value="all">Todas las organizaciones</SelectItem>
          {users.map((user) => (
            <SelectItem 
              key={user.id} 
              value={user.id}
            >
              {user.organization}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default UserSelector;
